/**
 * „Program", the create window's first step: start from nothing, or copy a library template — and
 * what the choice gives, drawn beside it.
 *
 * Canvas rows H (the plan library's „Nowy program") and I (the client detail's „Nowy plan"),
 * owner-approved on 23 September 2026. Two halves of the same `splitDialog` window the details step
 * stands in, so pressing „Dalej" changes what is inside the panes and never the window.
 *
 * ── One radio group, split by the search ─────────────────────────────────────────────────────
 * „Zacznij od zera" and every template row are ONE choice, so they share a native radio `name`:
 * the browser groups them across the divider and the search between them, Tab stops on the chosen
 * one, and the arrows walk all of them. The `fieldset` carries the question for a reader.
 *
 * ── The list holds its box in every state ────────────────────────────────────────────────────
 * Pending, failed, empty, no match and answered all draw inside the same reserve, so the answer
 * landing changes ink and never geometry — and the skeleton inside it is gated (150ms grace, 300ms
 * minimum life), forked on `gate.waiting` and never on the read's own `pending`.
 */

import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import { PatternWeek } from '~/entities/plan-draft';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { PlanMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { SearchField } from '~/shared/ui/SearchField';
import { planCategoryColour } from '~/shared/ui/planCategoryColour';
import choice from '~/shared/ui/choiceRow.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import photoBox from '~/shared/ui/photoBox.module.css';
import segmented from '~/shared/ui/segmented.module.css';
import skeleton from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';
import hiddenControl from '~/shared/ui/visuallyHiddenControl.module.css';

import type { CreatePlanFlow } from '../lib/useCreatePlanFlow';
import { TEMPLATE_FACETS, type TemplateChoice, type TemplateFacet } from '../model/templatePicker';

import dialog from './CreatePlanDialog.module.css';
import styles from './ProgramStep.module.css';

/** How many skeleton rows stand in the reserve while the library is open. */
const SKELETON_ROWS = [0, 1, 2, 3];

/** The empty days the scratch preview draws. */
const EMPTY_SLOTS = [0, 1, 2, 3, 4];

/** The facet's option → where its label lives. The two systems reuse the details step's own names. */
const FACET_COPY: Readonly<Record<TemplateFacet, string>> = {
  all: 'web:planCreate.pick.facet.all',
  date: 'web:planCreate.system.dateBased.title',
  selfPaced: 'web:planCreate.system.selfPaced.title',
};

interface ProgramStepProps {
  readonly flow: CreatePlanFlow;
}

/** The left half: the scratch row, the divider, the search, the facet, the templates. */
export function ProgramChoice({ flow }: ProgramStepProps) {
  const { t } = useTranslation();
  const startName = useId();
  const facetName = useId();
  const pinnedNote = useId();
  const { templates } = flow;
  const gate = useSkeletonGate(templates.status === 'pending');
  const scratchChosen = flow.templateId === null;
  const status =
    templates.status === 'error'
      ? t('web:planCreate.pick.error')
      : templates.status === 'ready'
        ? t('web:planCreate.pick.count', { count: templates.matches.length })
        : '';

  return (
    <fieldset className={styles.program}>
      <legend className={hidden.srOnly}>{t('web:planCreate.pick.startLabel')}</legend>

      <label
        className={`${String(styles.scratch)} ${
          scratchChosen ? `${String(styles.scratchOn)} ${String(choice.rowSelected)}` : ''
        }`}
      >
        <input
          className={hiddenControl.control}
          type="radio"
          name={startName}
          checked={scratchChosen}
          data-start-choice=""
          onChange={() => {
            flow.chooseStart(null);
          }}
        />
        <span className={choice.rowMark} aria-hidden="true" />
        <span className={styles.plus} aria-hidden="true">
          +
        </span>
        <span className={choice.rowText}>
          <span className={choice.rowTitle}>{t('web:planCreate.pick.scratchTitle')}</span>
          <span className={choice.rowMeta}>{t('web:planCreate.pick.scratchCaption')}</span>
        </span>
      </label>

      <p className={`${String(styles.eyebrow)} ${String(styles.divider)}`}>
        {t('web:planCreate.pick.divider')}
      </p>

      <div className={styles.searchRow}>
        <SearchField
          size="fill"
          value={templates.query}
          onChange={templates.setQuery}
          label={t('web:planCreate.pick.search')}
          placeholder={t('web:planCreate.pick.searchPlaceholder')}
          clearLabel={t('web:planCreate.pick.clearSearch')}
          disabled={templates.status !== 'ready'}
        />
      </div>

      <div className={segmented.seg} role="radiogroup" aria-label={t('web:planCreate.pick.facetLabel')}>
        {TEMPLATE_FACETS.map((facet) => {
          const selected = templates.facet === facet;
          return (
            <label
              key={facet}
              className={`${String(segmented.segOption)} ${selected ? String(segmented.segOptionOn) : ''}`}
            >
              <input
                className={hiddenControl.control}
                type="radio"
                name={facetName}
                checked={selected}
                onChange={() => {
                  templates.setFacet(facet);
                }}
              />
              {t(FACET_COPY[facet])}
            </label>
          );
        })}
      </div>

      {/* Always mounted; only the words change. A status inserted together with its text is not
        * reliably announced, and this one speaks on every keystroke in the search. */}
      <p className={hidden.srOnly} role="status">
        {status}
      </p>

      {/* The reserve. It holds its box in every state, so nothing below or beside it moves. */}
      <div className={styles.list} aria-busy={gate.waiting ? 'true' : undefined}>
        {gate.waiting ? (
          <>
            {gate.skeleton &&
              SKELETON_ROWS.map((row) => (
                <span key={row} className={`${String(skeleton.bar)} ${String(styles.skeletonRow)}`} />
              ))}
            <span className={hidden.srOnly}>{t('web:planCreate.pick.loading')}</span>
          </>
        ) : templates.status === 'error' ? (
          <div className={`${styles.state} ${gate.reveal}`}>
            <p className={styles.stateText}>{t('web:planCreate.pick.error')}</p>
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
              onClick={templates.retry}
            >
              {t('web:planCreate.pick.retry')}
            </button>
          </div>
        ) : templates.total === 0 ? (
          <div className={`${styles.state} ${gate.reveal}`}>
            <EmptyState mark={<PlanMarkIcon />} tone="action" title={t('web:planCreate.pick.empty')} />
          </div>
        ) : (
          <div className={`${String(styles.listBody)} ${gate.reveal}`}>
            {/* The chosen template the filters leave out, above what they leave. Still one of the
              * group's radios, and still the checked one — only not counted as a match. */}
            {templates.pinned !== null && (
              <div className={styles.pinned}>
                <span id={pinnedNote} className={styles.eyebrow}>
                  {t('web:planCreate.pick.pinned')}
                </span>
                <div className={choice.rows}>
                  <TemplateRow
                    row={templates.pinned}
                    selected={flow.templateId === templates.pinned.templateId}
                    onChoose={flow.chooseStart}
                    name={startName}
                    describedBy={pinnedNote}
                  />
                </div>
              </div>
            )}
            {templates.matches.length === 0 ? (
              <div className={styles.state}>
                <EmptyState
                  mark={<PlanMarkIcon />}
                  tone="neutral"
                  title={
                    templates.query.trim() === ''
                      ? t('web:planCreate.pick.noMatchFacet')
                      : t('web:planCreate.pick.noMatch', { query: templates.query.trim() })
                  }
                />
              </div>
            ) : (
              <div className={choice.rows}>
                {templates.matches.map((row) => (
                  <TemplateRow
                    key={row.templateId}
                    row={row}
                    selected={flow.templateId === row.templateId}
                    onChoose={flow.chooseStart}
                    name={startName}
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </fieldset>
  );
}

interface TemplateRowProps {
  readonly row: TemplateChoice;
  readonly selected: boolean;
  /** The flow's own setter, so every row receives the same function. */
  readonly onChoose: CreatePlanFlow['chooseStart'];
  /** The start group's radio `name`, shared with the scratch row. */
  readonly name: string;
  /** The pinned row's note, read with its radio. */
  readonly describedBy?: string;
}

/** One template as one of the start group's radios: its thumbnail, its words and its system. */
function TemplateRow({ row, selected, onChoose, name, describedBy }: TemplateRowProps) {
  return (
    <label className={`${String(choice.row)} ${selected ? String(choice.rowSelected) : ''}`}>
      <input
        className={hiddenControl.control}
        type="radio"
        name={name}
        checked={selected}
        aria-describedby={describedBy}
        data-start-choice=""
        onChange={() => {
          onChoose(row.templateId);
        }}
      />
      <span className={choice.rowMark} aria-hidden="true" />
      <span className={styles.templateBody}>
        <img
          className={`${String(photoBox.photo)} ${String(styles.thumb)}`}
          src={row.thumbUrl}
          alt=""
          loading="lazy"
        />
        <span className={choice.rowText}>
          <span className={`${String(choice.rowTitle)} ${String(styles.templateName)}`}>{row.name}</span>
          <span className={choice.rowMeta}>{row.meta}</span>
        </span>
      </span>
      <span className={choice.rowBadge}>{row.systemLabel}</span>
    </label>
  );
}

/** The right half: what the choice gives — an empty plan, or the template's cover, facts and week. */
export function ProgramPreviewPane({ flow }: ProgramStepProps) {
  const { t } = useTranslation();
  const { preview } = flow;
  const gate = useSkeletonGate(preview.kind === 'template' && preview.status === 'pending');

  if (preview.kind === 'scratch') {
    return (
      <div className={styles.preview}>
        <span className={styles.eyebrow}>{t('web:planCreate.pick.previewEyebrow')}</span>
        {/* An empty plan, drawn as the empty days the builder will open on. Decorative: the
          * sentence under it says the same thing in words. */}
        <div className={styles.emptyPlan} aria-hidden="true">
          {EMPTY_SLOTS.map((slot) => (
            <span key={slot} className={styles.emptySlot} />
          ))}
        </div>
        <h3 className={styles.previewTitle}>{t('web:planCreate.pick.scratchPreviewTitle')}</h3>
        <p className={styles.previewBody}>{t('web:planCreate.pick.scratchPreviewBody')}</p>
      </div>
    );
  }

  const { choice: template } = preview;
  return (
    <div className={styles.preview} style={planCategoryColour(template.category)}>
      <img className={`${String(photoBox.photo)} ${String(styles.hero)}`} src={template.heroUrl} alt="" />
      <h3 className={styles.previewTitle}>{template.name}</h3>
      <ul className={styles.facts} aria-label={t('web:planCreate.pick.previewEyebrow')}>
        {template.facts.map((fact) => (
          <li key={fact} className={choice.rowBadge}>
            {fact}
          </li>
        ))}
      </ul>

      {/* The week's read failing is drawn in the week's own box, so the retry swaps ink for ink. */}
      {!gate.waiting && preview.status === 'error' ? (
        <div className={`${styles.weekFailure} ${gate.reveal}`}>
          <p className={styles.stateText}>{t('web:planCreate.pick.previewError')}</p>
          <button
            type="button"
            className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
            onClick={preview.retry}
          >
            {t('web:planCreate.pick.retry')}
          </button>
        </div>
      ) : (
        <div aria-busy={gate.waiting ? 'true' : undefined}>
          <PatternWeek
            view={gate.waiting ? null : preview.week}
            skeleton={gate.skeleton}
            reveal={gate.reveal}
          />
          {gate.waiting ? (
            <span className={hidden.srOnly}>{t('web:planCreate.pick.previewLoading')}</span>
          ) : null}
        </div>
      )}

      {flow.recipient !== null && (
        <p className={dialog.note}>
          {t('web:planCreate.pick.clientNote', { name: flow.recipient.clientName })}
        </p>
      )}
    </div>
  );
}
